import { PairingBundle } from './types';

/**
 * Talks to the DomiHealth edge node's local HTTPS API.
 *
 * SECURITY NOTE — deliberately reduced protection for now: the edge node
 * presents a self-signed TLS certificate, unique per device, whose SPKI
 * fingerprint is only known at pairing time (`pairingStorage.ts`'s
 * `cert_spki_sha256`, from the edge node's own pairing.py). The standard
 * Cordova TLS-pinning plugin (`cordova-plugin-advanced-http`) only
 * supports pinning against a certificate FILE bundled into the app at
 * build time, not a hash learned at runtime — so it cannot implement
 * what this actually needs. Real dynamic SPKI pinning requires a small
 * custom native plugin (Kotlin/Java for Android, Swift for iOS) that has
 * not been built yet.
 *
 * Until that exists, this client runs `cordova.plugin.http` in `nocheck`
 * mode: TLS certificate validation is disabled entirely, so a device on
 * the same local network could in principle impersonate the edge node
 * (a MITM attack) instead of being caught the way the edge node's own
 * pairing design intends. This was a deliberate, explicit trade-off — a
 * working app now, rather than blocking on native plugin work that
 * can't be compiled or tested in this environment anyway.
 * `cert_spki_sha256` is still carried in the pairing bundle specifically
 * so a real pinning implementation can be dropped in later without
 * re-pairing every edge node.
 *
 * In a plain browser (no `window.cordova` — e.g. `yarn start:browser`
 * during development), this falls back to `fetch`. `fetch` cannot
 * bypass certificate errors from JavaScript at all: for that path to
 * reach a real edge node, the browser must already trust its
 * certificate (e.g. a developer opened `https://<host>:<port>/` once
 * and clicked through the browser's own security warning first).
 *
 * Cordova's own "browser" platform (`cordova build browser`, distinct
 * from the plain-browser case above — here `window.cordova` DOES
 * exist) hits the same `fetch` limitation for a different reason:
 * confirmed directly from `cordova-plugin-advanced-http`'s own browser
 * platform source (`setServerTrustMode` there unconditionally calls
 * its failure callback with "not supported on browser platform", same
 * for `uploadFile`/`uploadFiles`/`downloadFile`) — a real browser
 * can't bypass TLS certificate validation from JavaScript at all, so
 * this plugin's whole trust-bypass mechanism is a no-op there
 * regardless. `isNativeHttpAvailable` treats this platform as
 * "native HTTP not available" for that reason, routing it through the
 * same `fetch` path as the plain-browser case (whose same manual-
 * cert-trust requirement above applies here too).
 *
 * UNVERIFIED PLATFORM GAP (iOS): `cordova-plugin-advanced-http` uses
 * AFNetworking on iOS rather than going through `NSURLSession`/ATS the
 * way ordinary WebView requests do, per its own README — but that same
 * README explicitly does not document whether iOS's App Transport
 * Security still blocks the initial TLS handshake to a self-signed host
 * before the plugin's own trust override ever runs. This couldn't be
 * confirmed without a real iOS build/device, which this environment
 * doesn't have. If connections fail specifically on iOS (not Android),
 * check whether an ATS exception — e.g. `NSAllowsLocalNetworking` under
 * `NSAppTransportSecurity` in `config.xml`'s iOS `<edit-config>` for
 * `*-Info.plist` — is needed; deliberately not added speculatively here
 * since its exact required form couldn't be verified.
 */

export interface HttpResponse {
  status: number;
  data: string;
}

export interface BinaryHttpResponse {
  status: number;
  data: ArrayBuffer | null; // null on any non-2xx status
  contentType: string;
}

function baseUrl(bundle: PairingBundle): string {
  return `https://${bundle.host}:${bundle.port}`;
}

function authHeaders(bundle: PairingBundle): Record<string, string> {
  return { Authorization: `Bearer ${bundle.token}` };
}

function cordovaHttp(): any {
  const w = window as any;
  return w.cordova && w.cordova.plugin && w.cordova.plugin.http;
}

export function isNativeHttpAvailable(): boolean {
  const w = window as any;
  if (w.cordova?.platformId === 'browser') {
    // See this module's docstring — cordova-plugin-advanced-http's own
    // browser-platform implementation can't do TLS trust bypass (or
    // file upload) at all, confirmed in its source.
    return false;
  }
  return Boolean(cordovaHttp());
}

let insecureTrustModeConfigured = false;
let requestTimeoutConfigured = false;

/** Idempotent and safe to call before every request. No-op outside
 * Cordova. Exported mainly so tests can reset/observe it. */
export async function ensureInsecureTrustModeConfigured(): Promise<void> {
  const http = cordovaHttp();
  if (insecureTrustModeConfigured || !http) {
    return;
  }
  await new Promise<void>((resolve, reject) => {
    http.setServerTrustMode('nocheck', resolve, () =>
      reject(new Error('Failed to configure cordova-plugin-advanced-http trust mode')),
    );
  });
  insecureTrustModeConfigured = true;
}

/** Idempotent and safe to call before every request, same pattern as
 * `ensureInsecureTrustModeConfigured`. No-op outside Cordova.
 *
 * The plugin's own default timeout is short enough that a real device
 * hit it (2026-09-14): `/users/{id}/uploads/{uploadId}/complete`
 * reassembles a chunked upload and runs OCR + lab-value extraction
 * synchronously before responding, which can genuinely take longer
 * than a typical request timeout for a large multi-page document —
 * unlike the individual chunk uploads (small, fast) that led up to it.
 * `setRequestTimeout` is global, not per-request (`sendRequest`'s own
 * per-call `timeout` option isn't available on the `post`/`uploadFile`
 * paths `httpPostJson`/`httpUploadBytes` use), so this raises the
 * ceiling for every request rather than only the slow one — harmless
 * for fast requests, since a generous timeout only matters when
 * something would otherwise still be waiting at that point anyway.
 *
 * Raised from 180 to 300 (2026-09-19): even after the edge node started
 * chunking documents before LLM extraction (fixing a real bug where a
 * long document timed out and returned zero flagged lab values), a real
 * 28-page report still took 211s end-to-end, which would have exceeded
 * the old 180s ceiling and produced a client-side timeout on an upload
 * that the server was still successfully processing. */
export async function ensureRequestTimeoutConfigured(): Promise<void> {
  const http = cordovaHttp();
  if (requestTimeoutConfigured || !http) {
    return;
  }
  http.setRequestTimeout(300); // seconds
  requestTimeoutConfigured = true;
}

export function resetHttpClientStateForTests(): void {
  insecureTrustModeConfigured = false;
  requestTimeoutConfigured = false;
}

function cordovaRequest(
  method: 'get' | 'post',
  url: string,
  bodyOrParams: Record<string, unknown>,
  headers: Record<string, string>,
): Promise<HttpResponse> {
  const http = cordovaHttp();
  return new Promise((resolve, reject) => {
    const onSuccess = (response: HttpResponse) => resolve(response);
    // The plugin routes anything that isn't a 2xx (and network failures,
    // as status -1) through the failure callback rather than the success
    // one — funnel both into the same resolved shape so callers only
    // ever branch on `.status`, exactly like the fetch() fallback below.
    const onFailure = (response: HttpResponse & { error?: string }) =>
      resolve({ status: response.status, data: response.error ?? response.data ?? '' });
    try {
      if (method === 'get') {
        http.get(url, bodyOrParams, headers, onSuccess, onFailure);
      } else {
        http.post(url, bodyOrParams, headers, onSuccess, onFailure);
      }
    } catch (err) {
      reject(err);
    }
  });
}

async function fetchRequest(
  method: 'GET' | 'POST',
  url: string,
  body: unknown,
  headers: Record<string, string>,
  jsonBody: boolean,
): Promise<HttpResponse> {
  const response = await fetch(url, {
    method,
    headers: jsonBody ? { ...headers, 'Content-Type': 'application/json' } : headers,
    body: method === 'GET' ? undefined : jsonBody ? JSON.stringify(body) : (body as BodyInit),
  });
  const data = await response.text();
  return { status: response.status, data };
}

export async function httpGet(
  bundle: PairingBundle,
  path: string,
  params: Record<string, string> = {},
): Promise<HttpResponse> {
  const headers = authHeaders(bundle);
  if (isNativeHttpAvailable()) {
    await ensureInsecureTrustModeConfigured();
    await ensureRequestTimeoutConfigured();
    return cordovaRequest('get', `${baseUrl(bundle)}${path}`, params, headers);
  }
  const query = Object.keys(params).length ? `?${new URLSearchParams(params).toString()}` : '';
  return fetchRequest('GET', `${baseUrl(bundle)}${path}${query}`, undefined, headers, false);
}

export async function httpPostJson(
  bundle: PairingBundle,
  path: string,
  body: Record<string, unknown>,
): Promise<HttpResponse> {
  const headers = authHeaders(bundle);
  if (isNativeHttpAvailable()) {
    await ensureInsecureTrustModeConfigured();
    await ensureRequestTimeoutConfigured();
    cordovaHttp().setDataSerializer('json');
    return cordovaRequest('post', `${baseUrl(bundle)}${path}`, body, headers);
  }
  return fetchRequest('POST', `${baseUrl(bundle)}${path}`, body, headers, true);
}

function cordovaSendRequestForBinary(
  url: string,
  body: Record<string, unknown>,
  headers: Record<string, string>,
): Promise<BinaryHttpResponse> {
  const http = cordovaHttp();
  return new Promise((resolve, reject) => {
    const options = {
      method: 'post',
      data: body,
      headers,
      serializer: 'json',
      responseType: 'arraybuffer',
    };
    const onSuccess = (response: { status: number; data: ArrayBuffer; headers?: Record<string, string> }) => {
      const contentType = response.headers?.['content-type'] ?? response.headers?.['Content-Type'] ?? 'application/octet-stream';
      resolve({ status: response.status, data: response.data ?? null, contentType });
    };
    // Unlike the text-response paths, a failed binary response has no
    // meaningful "data" to funnel through (an ArrayBuffer isn't a place
    // to put an error string) — callers must check `.status` themselves,
    // same as they already have to for e.g. a 503 with no engine configured.
    const onFailure = (response: { status: number }) =>
      resolve({ status: response.status, data: null, contentType: 'application/octet-stream' });
    try {
      http.sendRequest(url, options, onSuccess, onFailure);
    } catch (err) {
      reject(err);
    }
  });
}

async function fetchRequestForBinary(
  url: string,
  body: Record<string, unknown>,
  headers: Record<string, string>,
): Promise<BinaryHttpResponse> {
  const response = await fetch(url, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const contentType = response.headers.get('content-type') ?? 'application/octet-stream';
  if (response.status < 200 || response.status >= 300) {
    return { status: response.status, data: null, contentType };
  }
  return { status: response.status, data: await response.arrayBuffer(), contentType };
}

/** Like `httpPostJson`, but for an endpoint that returns raw binary data
 * (e.g. `/voice/synthesize`'s audio) instead of JSON. Native path uses
 * `cordova.plugin.http.sendRequest` with `responseType: 'arraybuffer'`
 * (the documented way this plugin returns binary data — `post`/`get`
 * always decode the response as text, which would corrupt audio bytes). */
export async function httpPostJsonForBinary(
  bundle: PairingBundle,
  path: string,
  body: Record<string, unknown>,
): Promise<BinaryHttpResponse> {
  const headers = authHeaders(bundle);
  const url = `${baseUrl(bundle)}${path}`;
  if (isNativeHttpAvailable()) {
    await ensureInsecureTrustModeConfigured();
    await ensureRequestTimeoutConfigured();
    return cordovaSendRequestForBinary(url, body, headers);
  }
  return fetchRequestForBinary(url, body, headers);
}

function cordovaGetForBinary(url: string, headers: Record<string, string>): Promise<BinaryHttpResponse> {
  const http = cordovaHttp();
  return new Promise((resolve, reject) => {
    const options = { method: 'get', headers, responseType: 'arraybuffer' };
    const onSuccess = (response: { status: number; data: ArrayBuffer; headers?: Record<string, string> }) => {
      const contentType = response.headers?.['content-type'] ?? response.headers?.['Content-Type'] ?? 'application/octet-stream';
      resolve({ status: response.status, data: response.data ?? null, contentType });
    };
    const onFailure = (response: { status: number }) =>
      resolve({ status: response.status, data: null, contentType: 'application/octet-stream' });
    try {
      http.sendRequest(url, options, onSuccess, onFailure);
    } catch (err) {
      reject(err);
    }
  });
}

async function fetchGetForBinary(url: string, headers: Record<string, string>): Promise<BinaryHttpResponse> {
  const response = await fetch(url, { method: 'GET', headers });
  const contentType = response.headers.get('content-type') ?? 'application/octet-stream';
  if (response.status < 200 || response.status >= 300) {
    return { status: response.status, data: null, contentType };
  }
  return { status: response.status, data: await response.arrayBuffer(), contentType };
}

/** Like `httpGet`, but for an endpoint that returns raw binary data (e.g.
 * `/records/{id}/content`'s original uploaded file) instead of JSON —
 * GET counterpart to `httpPostJsonForBinary`, same reasoning: `get`
 * always decodes the response as text, which would corrupt binary
 * content such as an image or PDF. */
export async function httpGetForBinary(bundle: PairingBundle, path: string): Promise<BinaryHttpResponse> {
  const headers = authHeaders(bundle);
  const url = `${baseUrl(bundle)}${path}`;
  if (isNativeHttpAvailable()) {
    await ensureInsecureTrustModeConfigured();
    await ensureRequestTimeoutConfigured();
    return cordovaGetForBinary(url, headers);
  }
  return fetchGetForBinary(url, headers);
}

/** Uploads a single locally-stored file (a `file://` URI, as returned by
 * `navigator.camera.getPicture` with `destinationType: FILE_URI`) as
 * multipart form data alongside plain string fields. Native-only — a
 * plain browser has no comparable concept of an app-local file URI, and
 * `cordova-plugin-advanced-http` is what actually reads and streams it. */
export async function httpUploadFile(
  bundle: PairingBundle,
  path: string,
  fields: Record<string, string>,
  fileFieldName: string,
  fileUri: string,
): Promise<HttpResponse> {
  if (!isNativeHttpAvailable()) {
    throw new Error(
      'File upload requires the native app (cordova-plugin-advanced-http) — not available in the browser dev build.',
    );
  }
  const headers = authHeaders(bundle);
  await ensureInsecureTrustModeConfigured();
  await ensureRequestTimeoutConfigured();
  const http = cordovaHttp();
  http.setDataSerializer('multipart');
  return new Promise((resolve, reject) => {
    const onSuccess = (response: HttpResponse) => resolve(response);
    const onFailure = (response: HttpResponse & { error?: string }) =>
      resolve({ status: response.status, data: response.error ?? response.data ?? '' });
    try {
      http.uploadFile(
        `${baseUrl(bundle)}${path}`,
        fields,
        headers,
        [fileUri],
        [fileFieldName],
        onSuccess,
        onFailure,
      );
    } catch (err) {
      reject(err);
    }
  });
}

/** Hand-builds a multipart/form-data body (string fields plus one file
 * part) as raw bytes. Only `sendRequest`'s "raw" serializer (Uint8Array
 * or ArrayBuffer, per this plugin's own docs) needs this — `post`'s
 * "multipart" serializer only knows how to serialize plain field/value
 * pairs, not an in-memory file part. */
function encodeMultipartBody(
  boundary: string,
  fields: Record<string, string>,
  fileFieldName: string,
  fileName: string,
  fileMediaType: string,
  fileBytes: Uint8Array,
): Uint8Array {
  const encoder = new TextEncoder();
  const chunks: Uint8Array[] = [];
  const pushText = (text: string) => chunks.push(encoder.encode(text));

  for (const [name, value] of Object.entries(fields)) {
    pushText(`--${boundary}\r\n`);
    pushText(`Content-Disposition: form-data; name="${name}"\r\n\r\n`);
    pushText(`${value}\r\n`);
  }

  pushText(`--${boundary}\r\n`);
  pushText(`Content-Disposition: form-data; name="${fileFieldName}"; filename="${fileName}"\r\n`);
  pushText(`Content-Type: ${fileMediaType}\r\n\r\n`);
  chunks.push(fileBytes);
  pushText(`\r\n--${boundary}--\r\n`);

  const body = new Uint8Array(chunks.reduce((sum, chunk) => sum + chunk.length, 0));
  let offset = 0;
  for (const chunk of chunks) {
    body.set(chunk, offset);
    offset += chunk.length;
  }
  return body;
}

/** Browser fallback for `httpUploadBytes` — a real browser needs none of
 * that function's native-bridge workarounds at all: `FormData` + `fetch`
 * builds and sends a correct multipart body natively. Confirmed live
 * (2026-09-19): the browser-extension build (no `window.cordova` at
 * all, unlike `cordova build browser`) picked a file successfully (the
 * `filePicker.ts` browser fallback) but then failed to upload it,
 * surfaced only as this app's generic error message since
 * `httpUploadBytes` used to throw outright here. Deliberately does NOT
 * set a Content-Type header — `fetch` computes the multipart boundary
 * itself from the `FormData` body and needs to set that header, which
 * a manually-set one would silently break. */
async function fetchUploadBytes(
  bundle: PairingBundle,
  path: string,
  fields: Record<string, string>,
  fileFieldName: string,
  fileName: string,
  fileMediaType: string,
  fileBytes: Uint8Array,
): Promise<HttpResponse> {
  const formData = new FormData();
  for (const [name, value] of Object.entries(fields)) {
    formData.append(name, value);
  }
  formData.append(fileFieldName, new Blob([fileBytes], { type: fileMediaType }), fileName);
  const response = await fetch(`${baseUrl(bundle)}${path}`, {
    method: 'POST',
    headers: authHeaders(bundle),
    body: formData,
  });
  return { status: response.status, data: await response.text() };
}

/** Uploads raw in-memory bytes (e.g. from `cordova-plugin-chooser`,
 * which hands back a picked file's content directly rather than a
 * native `file://` path) as multipart form data.
 *
 * Exists specifically because `httpUploadFile`'s `uploadFile()` needs a
 * real, plugin-readable native path — confirmed against a real device
 * (2026-09-13) that neither `fileEntry.toURL()` nor a hand-constructed
 * `cacheDirectory`-relative path reliably produces one, so this
 * sidesteps writing (and then re-locating) a temporary file at all: the
 * multipart body is built by hand here and sent as raw bytes via
 * `sendRequest`'s "raw" data serializer, which this plugin documents as
 * accepting a `Uint8Array`/`ArrayBuffer` verbatim. */
export async function httpUploadBytes(
  bundle: PairingBundle,
  path: string,
  fields: Record<string, string>,
  fileFieldName: string,
  fileName: string,
  fileMediaType: string,
  fileBytes: Uint8Array,
): Promise<HttpResponse> {
  if (!isNativeHttpAvailable()) {
    return fetchUploadBytes(bundle, path, fields, fileFieldName, fileName, fileMediaType, fileBytes);
  }
  const boundary = `----DomiHealthBoundary${Date.now()}${Math.random().toString(16).slice(2)}`;
  const headers = {
    ...authHeaders(bundle),
    'Content-Type': `multipart/form-data; boundary=${boundary}`,
  };
  await ensureInsecureTrustModeConfigured();
  await ensureRequestTimeoutConfigured();
  const http = cordovaHttp();
  const body = encodeMultipartBody(boundary, fields, fileFieldName, fileName, fileMediaType, fileBytes);
  return new Promise((resolve, reject) => {
    const options = { method: 'post', data: body, headers, serializer: 'raw' };
    const onSuccess = (response: HttpResponse) => resolve(response);
    const onFailure = (response: HttpResponse & { error?: string }) =>
      resolve({ status: response.status, data: response.error ?? response.data ?? '' });
    try {
      http.sendRequest(`${baseUrl(bundle)}${path}`, options, onSuccess, onFailure);
    } catch (err) {
      reject(err);
    }
  });
}
